<template>
	<v-container fluid>
		<v-row align="end">
			<v-col cols="12" md="4">
				<v-autocomplete
					v-model="articulo"
					:items="articulos"
					label="Artículo"
					item-text="texto"
					item-value="idArticulo"
					:loading="loadingArticulos"
					clearable
					hide-details="auto"
					:disabled="loading || loadingArticulos"
					:error-messages="error"
					@input="limpiarReporte"
				></v-autocomplete>
			</v-col>
			<v-col cols="12" md="3">
				<v-btn
					color="blue darken-1"
					dark
					:loading="loading"
					:disabled="loading"
					@click="consultarReporte"
				>
					Consultar
				</v-btn>
			</v-col>
		</v-row>

		<v-row v-if="mensaje || error">
			<v-col cols="12" md="7">
				<v-alert v-if="mensaje" type="success" dense text>
					{{ mensaje }}
				</v-alert>
				<v-alert v-if="error" type="error" dense text>
					{{ error }}
				</v-alert>
			</v-col>
		</v-row>

		<v-row v-if="reporte">
			<v-col cols="12">
				<div class="caption grey--text text--darken-1">Artículo</div>
				<div class="subtitle-1 font-weight-bold">
					[{{ reporte.codigoArticulo }}] - {{ reporte.articulo }}
				</div>
			</v-col>
		</v-row>

		<v-row v-if="reporte && componentesConsolidados.length">
			<v-col cols="12" sm="4">
				<div class="metric-card">
					<div class="caption grey--text text--darken-1">Componentes</div>
					<div class="metric-value">{{ componentesConsolidados.length }}</div>
				</div>
			</v-col>
			<v-col cols="12" sm="4">
				<div class="metric-card">
					<div class="caption grey--text text--darken-1">ODT involucradas</div>
					<div class="metric-value">{{ reporte.odts.length }}</div>
				</div>
			</v-col>
			<v-col cols="12" sm="4">
				<div class="metric-card">
					<div class="caption grey--text text--darken-1">Almacenes con existencia</div>
					<div class="metric-value">{{ almacenesAgrupados.length }}</div>
				</div>
			</v-col>
		</v-row>

		<v-row v-if="reporte && componentesConsolidados.length">
			<v-col v-if="totalProducido > 0" cols="12" md="6">
				<div class="seccion-header-pt white--text">Producto terminado</div>
				<div class="caption grey--text mt-1 mb-2">
					Producido en piso
				</div>
				<div class="odt-detalle">
					<v-simple-table dense class="odt-detalle-table">
						<template v-slot:default>
							<thead>
								<tr>
									<th class="text-left">Artículo</th>
									<th class="text-right">Cantidad</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>
										<div class="font-weight-medium">
											{{ reporte.articulo }}
											<v-chip x-small label color="blue-grey lighten-4" text-color="blue-grey darken-3" class="ml-1">PT</v-chip>
										</div>
										<div class="caption grey--text">{{ reporte.codigoArticulo }}</div>
									</td>
									<td class="text-right font-weight-medium">{{ formatoNumero(totalProducido) }}</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</div>
			</v-col>
			<v-col
				v-for="alm in almacenesAgrupados"
				:key="'alm-' + alm.idAlmacen"
				cols="12"
				md="6"
			>
				<div class="seccion-header white--text">
					{{ alm.codigoAlmacen ? '[' + alm.codigoAlmacen + '] ' : '' }}{{ alm.nombreAlmacen }}
				</div>
				<div class="caption grey--text mt-1 mb-2">
					Disposición de {{ alm.tienePT ? 'artículos' : 'componentes' }} en este almacén
				</div>
				<div class="odt-detalle">
					<v-simple-table dense class="odt-detalle-table">
						<template v-slot:default>
							<thead>
								<tr>
									<th class="text-left">{{ alm.tienePT ? 'Artículo' : 'Componente' }}</th>
									<th class="text-right">Cantidad</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="art in alm.articulos" :key="art.key">
									<td>
										<div class="font-weight-medium">
											{{ art.nombre }}
											<v-chip v-if="art.esPT" x-small label color="blue-grey lighten-4" text-color="blue-grey darken-3" class="ml-1">PT</v-chip>
										</div>
										<div class="caption grey--text">{{ art.codigo }}{{ art.unidad ? ' · ' + art.unidad : '' }}</div>
									</td>
									<td class="text-right font-weight-medium">
										{{ formatoNumero(art.cantidad) }}{{ art.unidad ? ' ' + art.unidad : '' }}
									</td>
								</tr>
							</tbody>
						</template>
					</v-simple-table>
				</div>
			</v-col>

		</v-row>

		<v-row v-if="reporte && produccionEnProceso.length">
			<v-col cols="12">
				<div class="seccion-header white--text">Producto en proceso</div>
				<div class="caption grey--text mt-1 mb-3">
					Avance de surtido por ODT (requerido planeado vs enviado a producción) · clic en una ODT para ver el detalle
				</div>
				<v-expansion-panels accordion flat>
					<v-expansion-panel
						v-for="odt in produccionEnProceso"
						:key="odt.idTrabajoOrdenDetalle"
						class="mb-2 odt-panel"
					>
						<v-expansion-panel-header class="proceso-header white--text" :style="{ backgroundColor: estadoProceso(odt).color }">
							<div class="d-flex flex-wrap align-center flex-grow-1" style="gap: 16px;">
								<div class="d-flex align-center" style="min-width: 240px; flex: 1 1 240px;">
									<v-icon color="white" class="mr-2">{{ estadoProceso(odt).icon }}</v-icon>
									<div>
										<strong>{{ odt.ordenTrabajo }}</strong>
										<div class="caption white--text" style="opacity: .9;">
											Planeado: {{ formatoNumero(odt.planeado) }} · Producido: {{ formatoNumero(odt.producido) }} · {{ estadoProceso(odt).label }}
										</div>
									</div>
								</div>
								<div style="min-width: 170px; flex: 0 1 200px;">
									<div class="d-flex justify-space-between caption white--text" style="opacity: .9;">
										<span>{{ odt.surtidos }}/{{ odt.total }} surtidos</span>
										<span>{{ odt.cobertura }}%</span>
									</div>
									<v-progress-linear :value="odt.cobertura" color="white" background-color="rgba(255,255,255,.3)" height="6" rounded class="mt-1"></v-progress-linear>
								</div>
							</div>
						</v-expansion-panel-header>
						<v-expansion-panel-content>
							<div class="odt-detalle mt-3">
								<v-simple-table dense class="odt-detalle-table">
									<template v-slot:default>
										<thead>
											<tr>
												<th class="text-left">Componente</th>
												<th class="text-right">Requerido</th>
												<th class="text-right">Enviado</th>
												<th class="text-right">Faltante</th>
											</tr>
										</thead>
										<tbody>
											<tr v-for="c in odt.componentes" :key="odt.idTrabajoOrdenDetalle + '-' + c.idComponente">
												<td>
													<div class="d-flex align-center">
														<span class="estado-dot mr-2" :style="{ backgroundColor: c.faltante > 0 ? '#e24b4a' : '#1d9e75' }"></span>
														<div>
															<div class="font-weight-medium">{{ c.nombre }}</div>
															<div class="caption grey--text">{{ c.codigo }}{{ c.unidad ? ' · ' + c.unidad : '' }}</div>
														</div>
													</div>
												</td>
												<td class="text-right">{{ formatoNumero(c.requerido) }}</td>
												<td class="text-right font-weight-medium">{{ formatoNumero(c.enviado) }}</td>
												<td class="text-right" :class="c.faltante > 0 ? 'red--text text--darken-2 font-weight-medium' : 'green--text text--darken-2'">
													{{ formatoNumero(c.faltante) }}
												</td>
											</tr>
										</tbody>
									</template>
								</v-simple-table>
							</div>
						</v-expansion-panel-content>
					</v-expansion-panel>
				</v-expansion-panels>
			</v-col>
		</v-row>

		<v-row v-if="reporte && !componentesConsolidados.length">
			<v-col cols="12">
				<v-alert type="info" dense text>
					No se encontraron componentes relacionados.
				</v-alert>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import axios from 'axios'

export default {
	data() {
		return {
			articulo: null,
			articulos: [],
			reporte: null,
			loadingArticulos: false,
			loading: false,
			mensaje: null,
			error: null
		}
	},
	computed: {
		componentesConsolidados() {
			if (!this.reporte) {
				return []
			}

			const lista = this.reporte.componentes || this.reporte.Componentes

			if (Array.isArray(lista) && lista.length) {
				return lista.map((c) => ({
					idComponente: c.idComponente,
					componente: c.nombreComponente || 'SIN NOMBRE',
					codigoComponente: c.codigoComponente || '',
					unidad: c.unidad || '',
					existenciaActual: Number(c.existenciaActual || 0),
					almacenes: c.almacenes || c.Almacenes || []
				}))
			}

			if (!Array.isArray(this.reporte.odts)) {
				return []
			}

			const mapa = new Map()

			this.reporte.odts.forEach((odt) => {
				const componentes = Array.isArray(odt.componentes) ? odt.componentes : []

				componentes.forEach((componente) => {
					const idComponente = componente.idComponente

					if (!mapa.has(idComponente)) {
						mapa.set(idComponente, {
							idComponente,
							componente: componente.nombreComponente || 'SIN NOMBRE',
							codigoComponente: componente.codigoComponente || '',
							unidad: componente.unidad || '',
							existenciaActual: Number(componente.existenciaActual || 0),
							almacenes: componente.almacenes || componente.Almacenes || []
						})
					}
				})
			})

			return Array.from(mapa.values())
		},
		produccionPorOdt() {
			if (!this.reporte || !Array.isArray(this.reporte.odts)) {
				return []
			}
			return this.reporte.odts.map((odt) => ({
				idTrabajoOrdenDetalle: odt.idTrabajoOrdenDetalle,
				ordenTrabajo: this.getOdtNombre(odt),
				almacenesProductoTerminado: odt.almacenesProductoTerminado || odt.AlmacenesProductoTerminado || []
			}))
		},
		almacenesAgrupados() {
			const mapa = new Map()

			const ensure = (alm) => {
				const id = alm.idAlmacen
				if (!mapa.has(id)) {
					mapa.set(id, {
						idAlmacen: id,
						codigoAlmacen: alm.codigoAlmacen || '',
						nombreAlmacen: alm.nombreAlmacen || '',
						articulos: []
					})
				}
				return mapa.get(id)
			}

			this.componentesConsolidados.forEach((c) => {
				(c.almacenes || []).forEach((alm) => {
					ensure(alm).articulos.push({
						key: 'c-' + c.idComponente + '-' + alm.idAlmacen,
						codigo: c.codigoComponente,
						nombre: c.componente,
						unidad: c.unidad,
						cantidad: Number(alm.cantidad || 0),
						esPT: false
					})
				})
			})

			return Array.from(mapa.values())
				.map((g) => ({
					...g,
					articulos: g.articulos.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
				}))
				.sort((a, b) =>
					(a.codigoAlmacen || a.nombreAlmacen || '').localeCompare(b.codigoAlmacen || b.nombreAlmacen || '')
				)
		},
		totalProducido() {
			return this.reporte
				? Number(this.reporte.totalCantidadFabricadaPiso || this.reporte.TotalCantidadFabricadaPiso || 0)
				: 0
		},
		produccionEnProceso() {
			if (!this.reporte || !Array.isArray(this.reporte.odts)) {
				return []
			}
			return this.reporte.odts.map((odt) => {
				const planeado = Number(odt.cantidadPlaneada || 0)
				const componentes = (Array.isArray(odt.componentes) ? odt.componentes : []).map((c) => {
					const requerido = c.requeridoPlaneado != null
						? Number(c.requeridoPlaneado)
						: planeado * Number(c.cantidadReceta || 0)
					const enviado = Number(c.materialEnviadoProduccion || 0)
					return {
						idComponente: c.idComponente,
						nombre: c.nombreComponente || 'SIN NOMBRE',
						codigo: c.codigoComponente || '',
						unidad: c.unidad || '',
						requerido,
						enviado,
						faltante: Math.max(0, requerido - enviado)
					}
				})
				const total = componentes.length
				const surtidos = componentes.filter((c) => c.faltante <= 0).length
				return {
					idTrabajoOrdenDetalle: odt.idTrabajoOrdenDetalle,
					ordenTrabajo: this.getOdtNombre(odt),
					planeado,
					producido: Number(odt.cantidadFabricadaPiso || 0),
					componentes,
					total,
					surtidos,
					cobertura: total > 0 ? Math.round((surtidos / total) * 100) : 0,
					listo: total > 0 && surtidos === total
				}
			})
		}
	},
	mounted() {
		this.getArticulos()
	},
	methods: {
		limpiarReporte() {
			this.mensaje = null
			this.error = null
			this.reporte = null
		},
		getArticulos() {
			this.loadingArticulos = true

			axios
				.get('/Articulos', {
					params: {
						paginar: false
					}
				})
				.then(({ data }) => {
					this.articulos = this.getRows(data)
						.filter((articulo) => {
							const idArticuloTipo = articulo.IdArticuloTipo || articulo.idArticuloTipo

							return Number(idArticuloTipo) === 3
						})
						.map((articulo) => {
							const idArticulo = articulo.idArticulo || articulo.IdArticulo
							const codigo = articulo.codigo || articulo.Codigo || ''
							const nombre = articulo.nombre || articulo.Nombre || ''

							return {
								...articulo,
								idArticulo,
								texto: `[${codigo}] - ${nombre}`
							}
						})
				})
				.catch(() => {
					this.error = 'No se pudieron cargar los artículos'
				})
				.finally(() => {
					this.loadingArticulos = false
				})
		},
		getRows(data) {
			if (Array.isArray(data)) {
				return data
			}

			if (!data || typeof data !== 'object') {
				return []
			}

			const keys = ['items', 'Items', 'result', 'Result', 'results', 'Results', 'data', 'Data']

			for (const key of keys) {
				const rows = this.getRows(data[key])

				if (rows.length) {
					return rows
				}
			}

			return []
		},
		getOdtNombre(odt) {
			return odt.ordenTrabajo || `OT-${odt.idTrabajoOrden || odt.idTrabajoOrdenDetalle}`
		},
		estadoProceso(odt) {
			if (odt.listo) {
				return {
					color: '#2e7d32',
					icon: 'mdi-check-circle',
					label: 'Listo para producir ' + this.formatoNumero(odt.planeado)
				}
			}
			const porSurtir = odt.total - odt.surtidos
			const label = 'Faltan ' + porSurtir + ' de ' + odt.total + ' componentes'
			if (odt.cobertura < 50) {
				return { color: '#c62828', icon: 'mdi-alert', label }
			}
			return { color: '#ef6c00', icon: 'mdi-alert-circle', label }
		},
		consultarReporte() {
			this.mensaje = null
			this.error = null
			this.reporte = null

			if (!this.articulo) {
				this.error = 'Seleccione el artículo'
				return
			}

			this.loading = true

			axios
				.get('/Inventario/ReporteComponentesProduccion', {
					params: {
						idArticulo: this.articulo
					}
				})
				.then(({ data }) => {
					const odts = data && Array.isArray(data.odTs) ? data.odTs : (data.odts || data.ODTs || [])

					this.reporte = {
						...data,
						odts
					}
					this.mensaje = 'Se consultó con éxito, sin errores.'
				})
				.catch(() => {
					this.error = 'No se pudo consultar el reporte'
				})
				.finally(() => {
					this.loading = false
				})
		},
		formatoNumero(valor) {
			const numero = Number(valor || 0)

			return numero.toLocaleString('es-MX', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 4
			})
		}
	}
}
</script>

<style scoped>
.seccion-header {
	background-color: #378add;
	text-align: center;
	font-weight: 500;
	font-size: 13px;
	letter-spacing: 1px;
	padding: 8px;
	border-radius: 8px;
}

.seccion-header-pt {
	background-color: #37606e;
	text-align: center;
	font-weight: 500;
	font-size: 13px;
	letter-spacing: 1px;
	padding: 8px;
	border-radius: 8px;
}

.proceso-header {
	min-height: 40px;
	padding-top: 8px;
	padding-bottom: 8px;
}

.proceso-header ::v-deep .v-expansion-panel-header__icon .v-icon {
	color: #fff;
}

.odt-panel {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	overflow: hidden;
}

.estado-dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex: none;
	display: inline-block;
}

.metric-card {
	background-color: #f5f5f5;
	border-radius: 8px;
	padding: 12px 16px;
}

.metric-value {
	font-size: 22px;
	font-weight: 500;
}

.odt-detalle {
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	overflow: hidden;
}

.odt-detalle-table ::v-deep table {
	background-color: #fff;
}
</style>